import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Tags, Plus, MoreVertical, Pencil, Power, Trash2, Download, Sparkles, Loader2 } from "lucide-react";

const PRESETS = [
  { value: "ias1", label: "IAS 1 (current standard)" },
  { value: "ifrs18", label: "IFRS 18 (effective 1 Jan 2027)" },
];

function tierTag(c) {
  if (c.source !== "ifrs") return null;
  if (c.tier === 1) return { text: c.standard || "IFRS", cls: "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600" };
  if (c.tier === 2) return { text: "practice", cls: "bg-amber-50 dark:bg-amber-950/40 text-amber-600" };
  if (c.tier === 3) return { text: c.standard || "disclosure", cls: "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600" };
  return null;
}

export default function ConfigureCategories() {
  const { session } = useAuth();
  const canManage = session?.roles?.some((r) => ["partner", "manager", "tenant_admin"].includes(r));
  const [cats, setCats] = useState([]);
  const [preset, setPreset] = useState("ias1");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [renameTarget, setRenameTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([api.get("/m1/categories"), api.get("/m1/categories/preset")]);
      setCats(c.data); setPreset(p.data.preset);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addCustom = async () => {
    if (!name.trim()) return;
    try { await api.post("/m1/categories", { name: name.trim() }); setName(""); toast.success("Category added"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const changePreset = async (v) => {
    try { await api.put("/m1/categories/preset", { preset: v }); setPreset(v); toast.success("Reporting preset updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const loadDefaults = async () => {
    try {
      const r = await api.post("/m1/categories/load-defaults", {});
      toast.success(`Added ${r.data.added} IFRS categories${r.data.skipped ? `, skipped ${r.data.skipped} existing` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleActive = async (c) => {
    try { await api.patch(`/m1/categories/${c.id}`, { active: !(c.active !== false) }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const del = async (c) => {
    try { await api.delete(`/m1/categories/${c.id}`); toast.success("Category deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const active = cats.filter((c) => c.active !== false);
  const inactive = cats.filter((c) => c.active === false);

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <div className="overline text-azx-collection">Data Collection</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">Configure</h1>
        <p className="mt-1 text-sm text-slate-500">Tenant-wide request categories, inherited by every engagement. Statuses & phases live in Admin → Configuration.</p>
      </div>

      {/* Reporting preset */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
        <div className="flex items-center gap-2 mb-1"><Sparkles className="h-4 w-4 text-indigo-500" /><span className="font-heading font-semibold">Reporting framework</span></div>
        <p className="text-sm text-slate-500 mb-4">Controls which profit-or-loss categories the IFRS defaults use.</p>
        <div className="flex items-center gap-3">
          <Select value={preset} onValueChange={changePreset} disabled={!canManage}>
            <SelectTrigger className="w-72" data-testid="preset-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRESETS.map((p) => <SelectItem key={p.value} value={p.value} data-testid={`preset-${p.value}`}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Categories */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2"><Tags className="h-4 w-4 text-indigo-500" /><span className="font-heading font-semibold">Request categories</span><span className="text-xs text-slate-400">({active.length} active)</span></div>
          {canManage && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadDefaults} data-testid="load-ifrs-defaults"><Download className="mr-1.5 h-3.5 w-3.5" /> Load IFRS defaults</Button>
              <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="add-more-categories"><Plus className="mr-1.5 h-3.5 w-3.5" /> Add more</Button>
            </div>
          )}
        </div>

        {loading ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <>
            {canManage && (
              <div className="flex gap-2 mb-5">
                <Input data-testid="new-category" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCustom()} placeholder="Add a custom category…" />
                <Button onClick={addCustom} data-testid="add-category" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4" /></Button>
              </div>
            )}
            {active.length === 0 && <p className="text-sm text-slate-400 mb-4">No categories yet. Use “Load IFRS defaults” to seed the standard IAS 1 list, or add your own above.</p>}
            <div className="space-y-1.5 mb-5">
              {active.map((c) => {
                const tag = tierTag(c);
                return (
                  <div key={c.id} data-testid={`cat-row-${c.id}`} className="flex items-center justify-between rounded-md border border-slate-100 dark:border-slate-800 px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium truncate">{c.name}</span>
                      {tag && <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${tag.cls}`}>{tag.text}</span>}
                    </div>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><button data-testid={`cat-menu-${c.id}`} className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setRenameTarget(c)}><Pencil className="mr-2 h-4 w-4" /> Rename</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleActive(c)} data-testid={`cat-deactivate-${c.id}`}><Power className="mr-2 h-4 w-4" /> Deactivate</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => del(c)} className="text-red-600" data-testid={`cat-delete-${c.id}`}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                );
              })}
            </div>

            {inactive.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Deactivated</p>
                <div className="flex flex-wrap gap-2">
                  {inactive.map((c) => (
                    <button key={c.id} data-testid={`cat-reactivate-${c.id}`} onClick={() => canManage && toggleActive(c)} className="rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs text-slate-500 line-through hover:no-underline">{c.name}</button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <RenameDialog cat={renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)} onDone={() => { setRenameTarget(null); load(); }} />
      <AddCategoriesDialog open={addOpen} preset={preset} onOpenChange={setAddOpen} onDone={() => { setAddOpen(false); load(); }} />
    </div>
  );
}

function RenameDialog({ cat, onOpenChange, onDone }) {
  const [v, setV] = useState("");
  useEffect(() => { setV(cat?.name || ""); }, [cat]);
  if (!cat) return null;
  const save = async () => {
    try { await api.patch(`/m1/categories/${cat.id}`, { name: v.trim() }); toast.success("Renamed"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={!!cat} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cat-rename-dialog">
        <DialogHeader><DialogTitle>Rename category</DialogTitle><DialogDescription>Update the display name for this request category.</DialogDescription></DialogHeader>
        <div><Label>Name</Label><Input data-testid="cat-rename-input" className="mt-1.5" value={v} onChange={(e) => setV(e.target.value)} /></div>
        <DialogFooter><Button onClick={save} data-testid="cat-rename-save" disabled={!v.trim()}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddCategoriesDialog({ open, preset, onOpenChange, onDone }) {
  const [groups, setGroups] = useState([]);
  const [sel, setSel] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSel({}); setLoading(true);
    api.get(`/m1/categories/catalog?preset=${preset}`).then((r) => setGroups(r.data.groups))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [open, preset]);

  const toggle = (code) => setSel((s) => ({ ...s, [code]: !s[code] }));
  const chosen = Object.keys(sel).filter((k) => sel[k]);

  const apply = async () => {
    if (chosen.length === 0) return toast.error("Select at least one category");
    setBusy(true);
    try {
      const r = await api.post("/m1/categories/load-defaults", { codes: chosen, preset });
      toast.success(`Added ${r.data.added} categor${r.data.added === 1 ? "y" : "ies"}${r.data.skipped ? `, skipped ${r.data.skipped}` : ""}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-categories-dialog">
        <DialogHeader>
          <DialogTitle>Add IFRS categories</DialogTitle>
          <DialogDescription>Pick from the IAS 1 / IFRS 18 defaults, practical sub-categories, and disclosure notes. Already-added items are checked and disabled.</DialogDescription>
        </DialogHeader>
        {loading ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.key}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{g.label}</span>
                  <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-500">Tier {g.tier}</span>
                </div>
                <div className="space-y-1">
                  {g.items.map((it) => (
                    <label key={it.code} data-testid={`catalog-item-${it.code}`} className={`flex items-center gap-2.5 rounded px-2 py-1 ${it.present ? "opacity-50" : "hover:bg-slate-50 dark:hover:bg-slate-800/40"}`}>
                      <Checkbox checked={it.present || !!sel[it.code]} disabled={it.present} onCheckedChange={() => toggle(it.code)} />
                      <span className="text-sm">{it.name}</span>
                      {it.standard && <span className="text-[10px] text-emerald-600 font-semibold">{it.standard}</span>}
                      {it.present && <span className="text-[10px] text-slate-400 ml-auto">added</span>}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button onClick={apply} data-testid="add-categories-apply" disabled={busy || chosen.length === 0} className="bg-indigo-600 hover:bg-indigo-700">Add {chosen.length || ""} categor{chosen.length === 1 ? "y" : "ies"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
